import React, { useState } from "react";
import { Table, Button, Dropdown, Menu, Modal, Tooltip, message, Pagination } from "antd";
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';

const OrganizationDataTable = ({ 
    rows, 
    handleEditRow, 
    handleDeleteRow,
    page,
    totalItems,
    onPageChange
}) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [idTooltip, setIdTooltip] = useState("Copy ID");

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id).then(() => {
            setIdTooltip("Copied!");
            setTimeout(() => setIdTooltip("Copy ID"), 1500);
        });
        message.success("ID copied to clipboard!");
    };

    const handleDeleteClick = (id) => {
        setSelectedRowId(id);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setSelectedRowId(null);
    };

    const handleConfirmDelete = () => {
        handleDeleteRow(selectedRowId);
        handleDialogClose();
    };

    const handlePrevPage = () => {
        if (page > 1) {
            onPageChange(page - 1);
        }
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalItems / 15);
        if (page < totalPages) {
            onPageChange(page + 1);
        }
    };

    const columns = [
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' , }}>ID</span>,
            dataIndex: "id",
            key: "id",
            render: (text) => (
                <>
                    {text.slice(-10)}
                    <Tooltip title={idTooltip}>
                        <Button
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={() => handleCopyId(text)}
                            
                            style={{ marginLeft: 8 }}
                        />
                    </Tooltip>
                </>
            ),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Created At</span>,
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => {
                if (!text) return "N/A";
                const date = new Date(text);
                date.setHours(date.getHours() + 5);
                date.setMinutes(date.getMinutes() + 30);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-based
               const year = date.getFullYear();

            return `${day}/${month}/${year}`;
            },
         
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Name</span>,
            dataIndex: "name",
            key: "name",
            render: (text) => text || "N/A",
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Industry Type</span>,
            dataIndex: "industry_type",
            key: "industry_type",
            render: (text) => text || "N/A",
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>City</span>,
            dataIndex: "cities",
            key: "city",
            render: (cities) => (cities && cities[0] ? cities[0].name : "N/A"),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>No of Employees</span>,
            dataIndex: "no_employees",
            key: "no_employees",
            render: (text) => (text !== undefined ? text : "N/A"),
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>POC Name</span>,
            dataIndex: "poc_name",
            key: "poc_name",
            render: (text) => text || "N/A",
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>POC Email</span>,
            dataIndex: "poc_email",
            key: "poc_email",
            render: (text) => text || "N/A",
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>POC Contact</span>,
            dataIndex: "poc_contact",
            key: "poc_contact",
            render: (text) => text || "N/A",
        },
        {
            title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
            key: "actions",
            render: (_, record) => (
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item onClick={() => handleEditRow(record.id)}>Edit</Menu.Item>
                            <Menu.Item onClick={() => handleDeleteClick(record.id)}>Delete</Menu.Item>
                        </Menu>
                    }
                    trigger={["click"]}
                >
                    <Button icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div style={{ margin: 10 }}>
            <Table
                columns={columns}
                dataSource={rows}
                rowKey="id"
                size="small"
                pagination={false}
                rowClassName="custom-row"
                className="antTableClass"
                style={{ 
                    marginBottom: 0,
                    borderRadius: '8px 8px 0 0'
                }} 
                scroll={{ x: 2000, y: "60vh" }}
            />

            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px 8px',
                background: 'rgba(238, 238, 240, 1)',
                borderTop: '0.5px solid rgba(218, 218, 221, 1)',
                borderRadius: '0 0 8px 8px',
                marginTop: '-10px'
            }}>
                {/* Total count */}
                <div style={{ 
                    fontFamily: 'Inter', 
                    fontSize: '12px', 
                    fontWeight: 400, 
                    lineHeight: '18px', 
                    letterSpacing: '-0.1px', 
                    color: '#27272A',
                    marginLeft: '10px'
                }}>
                    Showing {Math.min(rows.length, 15)} of {totalItems} Org.
                </div>

                {/* Page numbers in center */}
                <Pagination
                    current={page}
                    total={totalItems}
                    pageSize={15}
                    showSizeChanger={false}
                    size="small"
                    style={{ margin: '0 auto' }}
                    onChange={onPageChange}
                    itemRender={(_, type, originalElement) => {
                        if (type === 'prev' || type === 'next') {
                            return null;
                        }
                        return originalElement;
                    }}
                    className="custom-pagination"
                />

                {/* Previous/Next buttons on right */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div 
                        onClick={handlePrevPage}
                        style={{ 
                            fontFamily: 'Inter', 
                            fontSize: '12px', 
                            fontWeight: 400, 
                            lineHeight: '18px', 
                            letterSpacing: '-0.1px', 
                            color: page > 1 ? '#666' : '#ccc', 
                            cursor: page > 1 ? 'pointer' : 'not-allowed', 
                            marginRight: '0px',
                            paddingRight: '16px' 
                        }}
                    >
                        ← Previous
                    </div>
                    <div 
                        style={{ 
                            borderLeft: '1px solid #d9d9d9',
                            marginLeft: '8px',
                            paddingLeft: '16px'
                        }}
                    >
                        <div 
                            onClick={handleNextPage}
                            style={{ 
                                fontFamily: 'Inter', 
                                fontSize: '12px', 
                                fontWeight: 400, 
                                lineHeight: '18px', 
                                letterSpacing: '-0.1px', 
                                color: page < Math.ceil(totalItems / 15) ? '#666' : '#ccc', 
                                cursor: page < Math.ceil(totalItems / 15) ? 'pointer' : 'not-allowed' 
                            }}
                        >
                            Next →
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title="Confirm Deletion"
                visible={openDialog}
                onCancel={handleDialogClose}
                onOk={handleConfirmDelete}
                okText="Confirm"
                cancelText="Cancel"
            >
                <p>Are you sure you want to delete this item?</p>
            </Modal>
        </div>
    );
};

export default OrganizationDataTable;

<style jsx global>{`
    .custom-pagination .ant-pagination-item {
        border: 1px solid lightblue !important;
    }
    .custom-pagination .ant-pagination-item-active {
        border: 1px solid lightblue !important;
        background-color: #f0f0f0 !important;
    }
    .custom-pagination .ant-pagination-item:focus,
    .custom-pagination .ant-pagination-item:hover {
        border: 1px solid lightblue !important;
    .custom-pagination .ant-pagination-item a {
        color: #666 !important;
    }
`}</style>
